import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { areEqual } from 'Utils/equalityChecks';
import { Api } from 'Utils/api';
import { Modal } from 'Components/Modal';

import { AddCompanyModal } from '../AddCompanyModal';
import { Option, CustomFieldConfig, ReferralPartnerFormData } from '../types';

import classes from './referralPartnerModal.module.css';

interface ReferralPartnerModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: string;
  ssoToken: string;
  companyOptions: Option[];
  customFieldConfig: CustomFieldConfig;
  isLoadingCompanies: boolean;
  onReferralPartnerCreated: (partner: Option) => void;
  onCompanyCreated: (company: Option) => void;
}

const defaultFormValues: ReferralPartnerFormData = {
  locationId: '',
  firstName: '',
  lastName: '',
  email: '',
  dialCode: '+1',
  phone: '',
  address: '',
  city: '',
  state: '',
  postalCode: '',
  companyId: '',
  companyName: '',
  industry: '',
};

function ReferralPartnerModalComponent({
  isOpen,
  onClose,
  locationId,
  ssoToken,
  companyOptions,
  customFieldConfig,
  isLoadingCompanies,
  onReferralPartnerCreated,
  onCompanyCreated,
}: ReferralPartnerModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm<ReferralPartnerFormData>({
    mode: 'onChange',
    defaultValues: defaultFormValues,
  });

  const selectedCompanyId = watch('companyId');

  const selectedCompanyLabel = useMemo(() => {
    if (!selectedCompanyId) return '';
    return companyOptions.find((option) => option.id === selectedCompanyId)?.label || '';
  }, [companyOptions, selectedCompanyId]);

  useEffect(() => {
    if (!isOpen) {
      setErrorMessage(null);
      setSuccessMessage(null);
      reset(defaultFormValues);
    }
  }, [isOpen, reset]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleCompanyChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setValue('companyId', value, { shouldValidate: true, shouldDirty: true });
    const label = companyOptions.find((option) => option.id === value)?.label || '';
    setValue('companyName', label);
  }, [companyOptions, setValue]);

  const handleCompanyCreated = useCallback((company: Option) => {
    onCompanyCreated(company);
    setValue('companyId', company.id, { shouldValidate: true, shouldDirty: true });
    setValue('companyName', company.label);
    setShowAddCompanyModal(false);
  }, [onCompanyCreated, setValue]);

  const onSubmit = handleSubmit(async (data) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const customFields: { id: string; field_value: string }[] = [];

      if (data.industry && customFieldConfig.industryId) {
        customFields.push({ id: customFieldConfig.industryId, field_value: data.industry });
      }

      const firstName = data.firstName.charAt(0).toUpperCase() + data.firstName.slice(1);
      const lastName = data.lastName.charAt(0).toUpperCase() + data.lastName.slice(1);

      // Backend expects snake_case field names
      const contactPayload: any = {
        first_name: firstName,
        last_name: lastName,
        location_id: locationId,
        type: 'referral_partner',
        custom_fields: customFields,
      };

      if (data.email?.trim()) {
        contactPayload.email = data.email;
      }
      if (data.phone?.trim()) {
        contactPayload.phone = `${data.dialCode} ${data.phone}`;
      }
      if (data.address?.trim()) {
        contactPayload.address1 = data.address;
      }
      if (data.city?.trim()) {
        contactPayload.city = data.city;
      }
      if (data.state?.trim()) {
        contactPayload.state = data.state;
      }
      if (data.postalCode?.trim()) {
        contactPayload.postal_code = data.postalCode;
      }
      if (selectedCompanyLabel) {
        contactPayload.company_name = selectedCompanyLabel;
      }
      if (data.companyId) {
        contactPayload.business_id = data.companyId;
      }

      const contactResponse = await Api.post('/ghl/embed/contacts', contactPayload, {
        headers: { 'X-GHL-SSO': ssoToken },
      });

      // Response format: { data: { id: "...", ... } }
      const newContactId = contactResponse?.data?.data?.id || contactResponse?.data?.id;

      if (!newContactId) {
        throw new Error('Failed to create referral partner');
      }

      const label = `${firstName} ${lastName}`.trim() || 'Referral Partner';

      onReferralPartnerCreated({
        id: newContactId,
        label,
      });

      setSuccessMessage('Referral partner created successfully!');

      setTimeout(() => {
        setSuccessMessage(null);
        reset(defaultFormValues);
      }, 3000);
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Failed to create referral partner', error);
      const message = error?.response?.data?.message || error?.message || 'Failed to create referral partner';
      const matchingField = error?.response?.data?.meta?.matchingField;
      setErrorMessage(`${message}${matchingField ? ` Matching ${matchingField} already exists.` : ''}`);
    } finally {
      setIsSubmitting(false);
    }
  });

  return (
    <>
      <Modal
        id="referral-partner-modal"
        classes={classes}
        title="Add Referral Partner"
        isOpen={isOpen}
        modalHeader
        modalCloseClick={handleClose}
      >
        {errorMessage && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {errorMessage}
            <button type="button" className="btn-close" onClick={() => setErrorMessage(null)} aria-label="Close" />
          </div>
        )}

        {successMessage && (
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            {successMessage}
            <button type="button" className="btn-close" onClick={() => setSuccessMessage(null)} aria-label="Close" />
          </div>
        )}

        <form onSubmit={onSubmit}>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="rp-firstName" className="form-label">
                First Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                id="rp-firstName"
                {...register('firstName', { required: 'First name is required' })}
              />
              {errors.firstName && <div className="invalid-feedback">{errors.firstName.message}</div>}
            </div>

            <div className="col-md-6 mb-3">
              <label htmlFor="rp-lastName" className="form-label">
                Last Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                id="rp-lastName"
                {...register('lastName', { required: 'Last name is required' })}
              />
              {errors.lastName && <div className="invalid-feedback">{errors.lastName.message}</div>}
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="rp-email" className="form-label">Email</label>
              <input
                type="email"
                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                id="rp-email"
                {...register('email', {
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: 'Invalid email format',
                  },
                })}
              />
              {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
            </div>

            <div className="col-md-6 mb-3">
              <label htmlFor="rp-phone" className="form-label">Phone</label>
              <div className="input-group">
                <select className="form-select" style={{ maxWidth: '80px' }} {...register('dialCode')}>
                  <option value="+1">+1</option>
                  <option value="+44">+44</option>
                  <option value="+61">+61</option>
                </select>
                <input
                  type="tel"
                  className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                  id="rp-phone"
                  placeholder="555-555-5555"
                  {...register('phone', {
                    pattern: {
                      value: /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/,
                      message: 'Invalid phone format',
                    },
                  })}
                />
              </div>
              {errors.phone && <div className="text-danger small">{errors.phone.message}</div>}
            </div>
          </div>

          <div className="mb-3">
            <label htmlFor="rp-address" className="form-label">Address</label>
            <input type="text" className="form-control" id="rp-address" {...register('address')} />
          </div>

          <div className="row">
            <div className="col-md-4 mb-3">
              <label htmlFor="rp-city" className="form-label">City</label>
              <input type="text" className="form-control" id="rp-city" {...register('city')} />
            </div>
            <div className="col-md-4 mb-3">
              <label htmlFor="rp-state" className="form-label">State</label>
              <input type="text" className="form-control" id="rp-state" {...register('state')} />
            </div>
            <div className="col-md-4 mb-3">
              <label htmlFor="rp-postalCode" className="form-label">Zip Code</label>
              <input type="text" className="form-control" id="rp-postalCode" {...register('postalCode')} />
            </div>
          </div>

          <div className="mb-3">
            <label htmlFor="rp-companyId" className="form-label">Company</label>
            <div className="input-group">
              <select
                className="form-select"
                id="rp-companyId"
                value={selectedCompanyId}
                onChange={handleCompanyChange}
                disabled={isLoadingCompanies}
              >
                <option value="">Select a company...</option>
                {companyOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setShowAddCompanyModal(true)}
                title="Add new company"
              >
                +
              </button>
            </div>
            {isLoadingCompanies && <div className={classes.helpText}>Loading companies...</div>}
          </div>

          {customFieldConfig.industryOptions.length > 0 && (
            <div className="mb-3">
              <label htmlFor="rp-industry" className="form-label">Industry</label>
              <select className="form-select" id="rp-industry" {...register('industry')}>
                {customFieldConfig.industryOptions.map((option) => (
                  <option key={option} value={option}>
                    {option || 'Select...'}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary w-100"
            disabled={!isDirty || !isValid || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Creating...
              </>
            ) : (
              'Create Referral Partner'
            )}
          </button>
        </form>
      </Modal>

      <AddCompanyModal
        isOpen={showAddCompanyModal}
        onClose={() => setShowAddCompanyModal(false)}
        locationId={locationId}
        ssoToken={ssoToken}
        onCompanyCreated={handleCompanyCreated}
      />
    </>
  );
}

const ReferralPartnerModalMemo = memo(ReferralPartnerModalComponent, areEqual);

export { ReferralPartnerModalMemo as ReferralPartnerModal };
